import api from '../api/apiClient';
import { emitSessionExpired } from '../auth/sessionExpiry';

const AI = '/ai-explorer/api/v1';

// Events the tool plane adds to query-stream, handed to options.onEvent as-is.
const TOOL_EVENTS = new Set(['tool_call', 'tool_result', 'approval_required', 'notice', 'error']);

/**
 * AI_BASE_URL is exported for backward compatibility. Components that use
 * fetch() for SSE streaming need a full base URL; we derive it from the
 * unified client's origin.
 */
export const AI_BASE_URL = (typeof window !== 'undefined' && window.location?.origin) || '';

// Get prepopulated insights (via AI Microservice)
export const getInsights = (params = {}) => {
  return api.get(`${AI}/ai-explorer/insights`, { params });
};

// Streaming query (Server-Sent Events) — via AI Microservice
export const queryStream = async (
  question,
  onToken,
  onSuggestions,
  onDone,
  onComplete,
  onDashboardGenerated,
  options = {}
) => {
  let response;
  try {
    const baseURL = AI_BASE_URL || window.location.origin;

    try {
      response = await fetch(`${baseURL}${AI}/ai-explorer/query-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('jwt_token')}`
        },
        // `signal` lets the agent page's Stop button cancel the stream; the
        // AbortError it raises is rethrown below like any other failure.
        signal: options?.signal,
        body: JSON.stringify({
          question,
          page_context: options?.page_context || null,
          conversation_history: options?.conversation_history || null,
          // Tool plane: answer with the platform's MCP tools; steps arrive as
          // tool_call / tool_result / approval_required events (onEvent).
          ...(options?.use_tools != null ? { use_tools: !!options.use_tools } : {}),
          ...(options?.tools ? { tools: options.tools } : {}),
        })
      });
    } catch (fetchError) {
      if (fetchError?.name === 'AbortError') throw fetchError;
      // Handle network errors, CORS errors, etc.
      console.error('Fetch error:', fetchError);
      throw new Error(`Network error: ${fetchError.message || 'Failed to connect to server'}`);
    }

    // Check if response exists and has ok property
    if (!response || typeof response !== 'object' || typeof response.ok !== 'boolean') {
      throw new Error('Invalid response received from server');
    }

    if (!response.ok) {
      if (response.status === 401) {
        emitSessionExpired('Your session has expired. Continue your current session or sign in again.');
        throw new Error('Unauthorized');
      }
      let errorText = 'Unknown error';
      try {
        if (response && typeof response.text === 'function') {
          errorText = await response.text();
        }
      } catch (e) {
        console.error('Error reading error response:', e);
        errorText = `HTTP ${response.status || 'unknown'}`;
      }
      throw new Error(`HTTP error! status: ${response.status || 'unknown'}, message: ${errorText}`);
    }

    if (!response.body || typeof response.body.getReader !== 'function') {
      throw new Error('Response body is not readable or does not support streaming');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let streamDone = false;

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (trimmedLine.startsWith('data: ')) {
            try {
              const jsonStr = trimmedLine.slice(6); // Remove 'data: ' prefix
              if (jsonStr) {
                const data = JSON.parse(jsonStr);

                if (data.type === 'token' && data.text && onToken) {
                  onToken(data.text);
                } else if (data.type === 'suggestions' && data.data && onSuggestions) {
                  onSuggestions(data.data);
                } else if (data.type === 'complete' && onComplete) {
                  // Handle complete event - response might be in data.response or data itself
                  const completeResponse = data.response || data;
                  if (completeResponse) {
                    onComplete(completeResponse);
                  }
                } else if (data.type === 'dashboard_generated' && onDashboardGenerated) {
                  onDashboardGenerated(data);
                } else if (TOOL_EVENTS.has(data.type) && options?.onEvent) {
                  options.onEvent(data);
                } else if (data.type === 'done') {
                  streamDone = true;
                  if (onDone) {
                    onDone();
                  }
                }
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e, trimmedLine);
            }
          }
        }
      }

      // Process any remaining buffer
      const trimmedBuffer = buffer.trim();
      if (trimmedBuffer.startsWith('data: ')) {
        try {
          const jsonStr = trimmedBuffer.slice(6);
          if (jsonStr) {
            const data = JSON.parse(jsonStr);
            if (data.type === 'token' && data.text && onToken) {
              onToken(data.text);
            } else if (data.type === 'suggestions' && data.data && onSuggestions) {
              onSuggestions(data.data);
            } else if (data.type === 'complete' && onComplete) {
              // Handle complete event - response might be in data.response or data itself
              const completeResponse = data.response || data;
              if (completeResponse) {
                onComplete(completeResponse);
              }
            } else if (data.type === 'done') {
              streamDone = true;
              if (onDone) {
                onDone();
              }
            }
          }
        } catch (e) {
          console.error('Error parsing final SSE data:', e);
        }
      }

      // Ensure done is called even if we didn't receive a done event
      if (!streamDone && onDone) {
        onDone();
      }
    } catch (streamError) {
      console.error('Error reading stream:', streamError);
      // Ensure done is called even on stream error
      if (onDone) {
        onDone();
      }
      throw streamError;
    } finally {
      try {
        reader.releaseLock();
      } catch (e) {
        // Reader might already be released or locked by another reader
        console.debug('Could not release reader lock:', e);
      }
    }
  } catch (error) {
    if (error?.name !== 'AbortError') console.error('Error in queryStream:', error);
    // Call onDone to clear loading state even on error
    if (onDone) {
      onDone();
    }
    throw error;
  }
};

// Non-streaming query (via AI Microservice)
export const query = (data) => {
  return api.post(`${AI}/ai-explorer/query`, data);
};

// Conversation management (via AI Microservice)
export const saveConversation = (conversationData) => {
  return api.post(`${AI}/ai-explorer/conversation`, conversationData);
};

export const getConversation = (conversationId) => {
  return api.get(`${AI}/ai-explorer/conversation/${conversationId}`);
};

// The caller's most recent conversation for a surface/mode ({ conversation: null } if none).
export const getLatestConversation = ({ surface = 'chat_panel', agent_mode } = {}) => {
  return api.get(`${AI}/ai-explorer/conversation/latest`, { params: { surface, agent_mode } });
};

// History management (via AI Microservice)
export const getHistory = (params = {}) => {
  return api.get(`${AI}/ai-explorer/history`, { params });
};

export const deleteHistoryItem = (historyId) => {
  return api.delete(`${AI}/ai-explorer/history?id=${historyId}`);
};

// Bookmark management (via AI Microservice)
export const toggleBookmark = (historyId, bookmarked) => {
  return api.put(`${AI}/ai-explorer/bookmark/${historyId}`, { bookmarked });
};

// Feedback (via AI Microservice)
export const submitFeedback = (feedbackData) => {
  return api.post(`${AI}/ai-explorer/feedback`, feedbackData);
};

// The platform tools the agent can use for this caller ({ sources: [{ id, label, tools: [{ name, description, write }] }] }).
export const getAgentTools = () => {
  return api.get(`${AI}/ai-explorer/tools`);
};

// Run one tool the user approved in the UI (the only way a write tool executes).
export const callAgentTool = ({ name, arguments: args, conversation_id }) => {
  return api.post(`${AI}/ai-explorer/tool-call`, { name, arguments: args || {}, conversation_id: conversation_id || undefined });
};

// Audio transcription (via AI Microservice — uses configured LLM provider)
export const transcribeAudio = (audioBlob) => {
  const formData = new FormData();
  const ext = audioBlob.type?.includes('wav') ? 'wav' : 'webm';
  formData.append('file', audioBlob, `recording.${ext}`);
  return api.post(`${AI}/ai-explorer/transcribe`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30000,
  });
};


/**
 * Reads an SSE stream from the analyst into callbacks.
 *
 * Deliberately separate from queryStream's inline reader: that function backs
 * the AI Explorer page and is left untouched. New callers use this.
 */
async function readAnalystStream(response, { onToken, onComplete, onError } = {}) {
  if (!response.body || typeof response.body.getReader !== 'function') {
    throw new Error('Response body is not readable');
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const handle = (line) => {
    if (!line.startsWith('data: ')) return false;
    let evt;
    try {
      evt = JSON.parse(line.slice(6));
    } catch {
      return false;                       // a partial frame; the buffer keeps it
    }
    if (evt.type === 'token' && evt.text) onToken?.(evt.text);
    else if (evt.type === 'complete') onComplete?.(evt.response || evt);
    else if (evt.type === 'error') {
      const err = new Error(evt.message || 'Analyst error');
      // The server marks a failure that arrived after text had already
      // streamed; the two need different wording on screen.
      err.partial = !!evt.partial;
      onError?.(err);
    }
    return evt.type === 'done';
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (handle(line.trim())) return;
    }
  }
  handle(buffer.trim());
}

/**
 * Ask the analyst about ONE record the caller already has on screen.
 *
 * Backed by POST /ai-explorer/subject-query-stream, which exists because
 * query-stream has to *infer* what a question is about: a question it cannot
 * place becomes an estate-wide executive overview, and a definition-shaped one
 * takes its conceptual path, where the page's own metrics are dropped in favour
 * of a terminology block. Handing over the record removes the guesswork.
 *
 * `subject` is { kind, title, summary, attributes, measurements[], thresholds,
 * source } — see SubjectQueryRequest in the AI microservice.
 */
export const subjectQueryStream = async (
  question,
  subject,
  { onToken, onComplete, onError, conversationHistory, includePlatformData } = {},
) => {
  const baseURL = AI_BASE_URL || window.location.origin;
  const response = await fetch(`${baseURL}${AI}/ai-explorer/subject-query-stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
    },
    body: JSON.stringify({
      question,
      subject,
      conversation_history: conversationHistory || null,
      include_platform_data: !!includePlatformData,
    }),
  });

  if (response.status === 401) {
    emitSessionExpired('Your session has expired. Continue your current session or sign in again.');
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    throw new Error(`Analyst unavailable (HTTP ${response.status})`);
  }

  await readAnalystStream(response, { onToken, onComplete, onError });
};
